import { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Wallet, Receipt, HandCoins, Check, X, ChevronLeft, ChevronRight,
  DollarSign, Trash2, Eye, Download, FileSpreadsheet
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminFinance = () => {
  const { token } = useOutletContext();
  const [contributions, setContributions] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [lending, setLending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('contributions');
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState('');
  const limit = 20;

  useEffect(() => {
    if (activeTab === 'contributions') fetchContributions();
    else if (activeTab === 'expenses') fetchExpenses();
    else if (activeTab === 'lending') fetchLending();
  }, [activeTab, page, statusFilter]);

  const fetchContributions = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        token,
        skip: page * limit,
        limit
      });
      if (statusFilter) params.append('status', statusFilter);
      
      const response = await axios.get(`${API}/admin/contributions?${params}`);
      setContributions(response.data.contributions);
      setTotal(response.data.total);
    } catch (error) {
      toast.error('Failed to fetch contributions');
    } finally {
      setLoading(false);
    }
  };

  const fetchExpenses = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        token,
        skip: page * limit,
        limit
      });
      
      const response = await axios.get(`${API}/admin/expenses?${params}`);
      setExpenses(response.data.expenses);
      setTotal(response.data.total);
    } catch (error) {
      toast.error('Failed to fetch expenses');
    } finally {
      setLoading(false);
    }
  };

  const fetchLending = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        token,
        skip: page * limit,
        limit
      });
      if (statusFilter) params.append('status', statusFilter);
      
      const response = await axios.get(`${API}/admin/lending?${params}`);
      setLending(response.data.lending_requests);
      setTotal(response.data.total);
    } catch (error) {
      toast.error('Failed to fetch lending requests');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyContribution = async (contributionId, action) => {
    try {
      await axios.put(`${API}/admin/contributions/${contributionId}/verify?token=${token}&action=${action}`);
      toast.success(`Contribution ${action === 'approve' ? 'approved' : 'rejected'}`);
      fetchContributions();
    } catch (error) {
      toast.error('Failed to verify contribution');
    }
  };

  const handleDeleteExpense = async (expenseId) => {
    if (!confirm('Delete this expense?')) return;
    try {
      await axios.delete(`${API}/admin/expenses/${expenseId}?token=${token}`);
      toast.success('Expense deleted');
      fetchExpenses();
    } catch (error) {
      toast.error('Failed to delete expense');
    }
  };

  const handleExportTransactions = async (format) => {
    try {
      toast.loading(`Exporting transactions as ${format.toUpperCase()}...`);
      const response = await axios.get(`${API}/admin/export/transactions?token=${token}&format=${format}`, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { 
        type: format === 'csv' ? 'text/csv' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `transactions_export.${format}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.dismiss();
      toast.success(`Transactions exported as ${format.toUpperCase()}`);
    } catch (error) {
      toast.dismiss();
      toast.error('Failed to export transactions');
    }
  };

  const totalPages = Math.ceil(total / limit);

  const Pagination = () => totalPages > 1 && (
    <div className="px-6 py-4 border-t border-slate-800 flex items-center justify-between">
      <span className="text-sm text-slate-400">
        Page {page + 1} of {totalPages}
      </span>
      <div className="flex gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setPage(Math.max(0, page - 1))}
          disabled={page === 0}
          className="border-slate-700"
        >
          <ChevronLeft className="w-4 h-4" />
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
          disabled={page >= totalPages - 1}
          className="border-slate-700"
        >
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Export Section */}
      <div className="flex justify-end gap-3">
        <Button
          variant="outline"
          size="sm"
          onClick={() => handleExportTransactions('csv')}
          className="border-slate-700 text-slate-300 hover:text-white"
          data-testid="export-transactions-csv-btn"
        >
          <Download className="w-4 h-4 mr-2" />
          Export CSV
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => handleExportTransactions('xlsx')}
          className="border-slate-700 text-slate-300 hover:text-white"
          data-testid="export-transactions-excel-btn"
        >
          <FileSpreadsheet className="w-4 h-4 mr-2" />
          Export Excel
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); setPage(0); setStatusFilter(''); }}>
        <TabsList className="bg-slate-800 border border-slate-700">
          <TabsTrigger value="contributions" className="data-[state=active]:bg-orange-500/20 data-[state=active]:text-orange-400">
            <Wallet className="w-4 h-4 mr-2" /> Contributions
          </TabsTrigger>
          <TabsTrigger value="expenses" className="data-[state=active]:bg-orange-500/20 data-[state=active]:text-orange-400">
            <Receipt className="w-4 h-4 mr-2" /> Expenses
          </TabsTrigger>
          <TabsTrigger value="lending" className="data-[state=active]:bg-orange-500/20 data-[state=active]:text-orange-400">
            <HandCoins className="w-4 h-4 mr-2" /> Lending
          </TabsTrigger>
        </TabsList>

        {/* Contributions Tab */}
        <TabsContent value="contributions">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-slate-900 rounded-2xl border border-slate-800 overflow-hidden"
          >
            <div className="p-4 border-b border-slate-800 flex justify-between items-center">
              <select
                value={statusFilter}
                onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
                className="px-4 py-2 bg-slate-800 border border-slate-700 text-white rounded-xl"
              >
                <option value="">All Status</option>
                <option value="pending">Pending</option>
                <option value="verified">Verified</option>
                <option value="rejected">Rejected</option>
              </select>
              <span className="text-sm text-slate-400">{total} total</span>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-800/50">
                  <tr>
                    <th className="text-left px-6 py-4 text-slate-400 font-medium text-sm">User</th>
                    <th className="text-left px-6 py-4 text-slate-400 font-medium text-sm">Group</th>
                    <th className="text-left px-6 py-4 text-slate-400 font-medium text-sm">Amount</th>
                    <th className="text-left px-6 py-4 text-slate-400 font-medium text-sm">Status</th>
                    <th className="text-left px-6 py-4 text-slate-400 font-medium text-sm">Date</th>
                    <th className="text-right px-6 py-4 text-slate-400 font-medium text-sm">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800">
                  {loading ? (
                    <tr>
                      <td colSpan="6" className="px-6 py-8 text-center">
                        <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto" />
                      </td>
                    </tr>
                  ) : contributions.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="px-6 py-8 text-center text-slate-400">
                        No contributions found
                      </td>
                    </tr>
                  ) : (
                    contributions.map((c) => (
                      <tr key={c.id} className="hover:bg-slate-800/30 transition-colors">
                        <td className="px-6 py-4">
                          <p className="text-white">{c.user?.name || 'Unknown'}</p>
                          <p className="text-xs text-slate-500">{c.user?.phone}</p>
                        </td>
                        <td className="px-6 py-4 text-slate-300">{c.group?.name || 'Unknown'}</td>
                        <td className="px-6 py-4">
                          <span className="text-green-400 font-bold">₹{c.amount?.toLocaleString()}</span>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            c.status === 'verified' ? 'bg-green-500/20 text-green-400' :
                            c.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                            'bg-red-500/20 text-red-400'
                          }`}>
                            {c.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-slate-400 text-sm">
                          {new Date(c.created_at).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4">
                          {c.status === 'pending' && (
                            <div className="flex items-center justify-end gap-2">
                              <Button
                                size="sm"
                                onClick={() => handleVerifyContribution(c.id, 'approve')}
                                className="bg-green-500/20 text-green-400 hover:bg-green-500/30"
                              >
                                <Check className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                onClick={() => handleVerifyContribution(c.id, 'reject')}
                                className="bg-red-500/20 text-red-400 hover:bg-red-500/30"
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <Pagination />
          </motion.div>
        </TabsContent>

        {/* Expenses Tab */}
        <TabsContent value="expenses">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-slate-900 rounded-2xl border border-slate-800 overflow-hidden"
          >
            <div className="p-4 border-b border-slate-800 flex justify-end">
              <span className="text-sm text-slate-400">{total} total</span>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-800/50">
                  <tr>
                    <th className="text-left px-6 py-4 text-slate-400 font-medium text-sm">Description</th>
                    <th className="text-left px-6 py-4 text-slate-400 font-medium text-sm">Paid By</th>
                    <th className="text-left px-6 py-4 text-slate-400 font-medium text-sm">Group</th>
                    <th className="text-left px-6 py-4 text-slate-400 font-medium text-sm">Amount</th>
                    <th className="text-left px-6 py-4 text-slate-400 font-medium text-sm">Category</th>
                    <th className="text-left px-6 py-4 text-slate-400 font-medium text-sm">Date</th>
                    <th className="text-right px-6 py-4 text-slate-400 font-medium text-sm">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800">
                  {loading ? (
                    <tr>
                      <td colSpan="7" className="px-6 py-8 text-center">
                        <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto" />
                      </td>
                    </tr>
                  ) : expenses.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="px-6 py-8 text-center text-slate-400">
                        No expenses found
                      </td>
                    </tr>
                  ) : (
                    expenses.map((e) => (
                      <tr key={e.id} className="hover:bg-slate-800/30 transition-colors">
                        <td className="px-6 py-4 text-white">{e.description}</td>
                        <td className="px-6 py-4 text-slate-300">{e.paid_by_user?.name || 'Unknown'}</td>
                        <td className="px-6 py-4 text-slate-300">{e.group?.name || 'Unknown'}</td>
                        <td className="px-6 py-4">
                          <span className="text-orange-400 font-bold">₹{e.total_amount?.toLocaleString()}</span>
                        </td>
                        <td className="px-6 py-4">
                          <span className="px-2 py-1 rounded-full text-xs bg-slate-700 text-slate-300 capitalize">
                            {e.category}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-slate-400 text-sm">
                          {new Date(e.created_at).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteExpense(e.id)}
                            className="text-red-400 hover:text-red-300"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <Pagination />
          </motion.div>
        </TabsContent>

        {/* Lending Tab */}
        <TabsContent value="lending">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-slate-900 rounded-2xl border border-slate-800 overflow-hidden"
          >
            <div className="p-4 border-b border-slate-800 flex justify-between items-center">
              <select
                value={statusFilter}
                onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
                className="px-4 py-2 bg-slate-800 border border-slate-700 text-white rounded-xl"
              >
                <option value="">All Status</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="repaid">Repaid</option>
                <option value="rejected">Rejected</option>
              </select>
              <span className="text-sm text-slate-400">{total} total</span>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-800/50">
                  <tr>
                    <th className="text-left px-6 py-4 text-slate-400 font-medium text-sm">From Group</th>
                    <th className="text-left px-6 py-4 text-slate-400 font-medium text-sm">To User</th>
                    <th className="text-left px-6 py-4 text-slate-400 font-medium text-sm">Amount</th>
                    <th className="text-left px-6 py-4 text-slate-400 font-medium text-sm">Interest</th>
                    <th className="text-left px-6 py-4 text-slate-400 font-medium text-sm">Duration</th>
                    <th className="text-left px-6 py-4 text-slate-400 font-medium text-sm">Status</th>
                    <th className="text-left px-6 py-4 text-slate-400 font-medium text-sm">Date</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800">
                  {loading ? (
                    <tr>
                      <td colSpan="7" className="px-6 py-8 text-center">
                        <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto" />
                      </td>
                    </tr>
                  ) : lending.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="px-6 py-8 text-center text-slate-400">
                        No lending requests found
                      </td>
                    </tr>
                  ) : (
                    lending.map((l) => (
                      <tr key={l.id} className="hover:bg-slate-800/30 transition-colors">
                        <td className="px-6 py-4 text-white">{l.from_group?.name || 'Unknown'}</td>
                        <td className="px-6 py-4 text-slate-300">{l.to_user?.name || 'Unknown'}</td>
                        <td className="px-6 py-4">
                          <span className="text-blue-400 font-bold">₹{l.amount?.toLocaleString()}</span>
                        </td>
                        <td className="px-6 py-4 text-slate-300">{l.interest_rate}%</td>
                        <td className="px-6 py-4 text-slate-300">{l.duration_days} days</td>
                        <td className="px-6 py-4">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            l.status === 'approved' ? 'bg-green-500/20 text-green-400' :
                            l.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                            l.status === 'repaid' ? 'bg-blue-500/20 text-blue-400' :
                            'bg-red-500/20 text-red-400'
                          }`}>
                            {l.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-slate-400 text-sm">
                          {new Date(l.created_at).toLocaleDateString()}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <Pagination />
          </motion.div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminFinance;
